import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

const ConfirmContext = createContext(null);

const DEFAULT = { open: false, title: "Are you sure?", description: "This action cannot be undone.", confirmText: "Delete", cancelText: "Cancel", tone: "destructive" };

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(DEFAULT);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState({ ...DEFAULT, open: true, ...opts });
  }), []);

  const close = (val) => {
    setState((s) => ({ ...s, open: false }));
    if (resolver.current) { resolver.current(val); resolver.current = null; }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <div className="flex items-start gap-3">
              {state.tone === "destructive" && (
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </span>
              )}
              <div className="space-y-1 text-left">
                <AlertDialogTitle data-testid="confirm-title">{state.title}</AlertDialogTitle>
                <AlertDialogDescription data-testid="confirm-description">{state.description}</AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" onClick={() => close(false)} data-testid="confirm-cancel">{state.cancelText}</Button>
            <Button
              className={state.tone === "destructive" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : "bg-primary hover:bg-primary/90"}
              onClick={() => close(true)}
              data-testid="confirm-accept"
            >
              {state.confirmText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
